/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { sanitizeSvg } from 'projects/app-core/src/app/utils/sanitize-html.util';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import {
  AnyoS3Buckets,
  FileUtilsService,
} from 'projects/app-core/src/service/fileService';
import { forkJoin, lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  DropdownOptionsV2,
  FieldSpec,
  MasterDataCategoryV2,
} from '../../../models/master-data-config';
import {
  LyfisMasterDataResponse,
  MasterDataResponse,
} from '../../../models/masterDataResponse';
import { ListenerService } from '../../../services/listener.service';
import { MasterDataConfigService } from '../../../services/master-data-config.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { TherapistService } from '../../../services/therapist.service';

interface DropdownOptions {
  value: string;
  data: string;
}

@Component({
  selector: 'app-manage-master-data',
  templateUrl: './manage-master-data.component.html',
  styleUrl: './manage-master-data.component.scss',
})
export class ManageMasterDataComponent
  extends AnyoComponent
  implements OnInit, OnDestroy
{
  // #region State
  partners: DropdownOptions[] = [];
  experts: DropdownOptions[] = [];
  therapists: DropdownOptions[] = [];
  listeners: DropdownOptions[] = [];
  masterData: DropdownOptions[] = [];
  anyoRules: DropdownOptions[] = [];
  allPossibleOptions: DropdownOptions[] = [];
  private lyfisCache?: LyfisMasterDataResponse;
  private masterCache?: MasterDataResponse;

  isDrawerVisible = false;
  isSubmitting = false;
  category!: MasterDataCategoryV2;
  form: FormGroup;
  list: Array<{ m_id: string; data: string; [key: string]: unknown }> = [];
  private originalList: Array<{
    m_id: string;
    data: string;
    [key: string]: unknown;
  }> = [];
  displayKeys: string[] = [];
  filteredKeys: string[] = [];
  private editingId?: string;
  tableLoading = false;
  previewUrls: { [index: number]: string } = {};
  private svgPreviewCache = new Map<string, SafeHtml>();
  private svgPreviewLoading = new Set<string>();
  // Field-specific dropdown options to avoid races/overwrites between parallel loads
  fieldOptions: { [index: number]: DropdownOptions[] } = {};
  // trackBy helpers
  trackByRow = (_: number, row: { m_id: string }) => row.m_id;
  trackByKey = (_: number, key: string) => key;
  trackByIndex = (index: number) => index;

  constructor(
    private route: ActivatedRoute,
    private service: MasterDataConfigService,
    private fb: FormBuilder,
    auth: AuthService,
    private fileService: FileUtilsService,
    private toast: ToastService,
    private masterDataService: MasterdataService,
    private therapistService: TherapistService,
    private listenerService: ListenerService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {
    super(auth);
    this.form = this.fb.group({ fields: new FormArray<FormGroup>([]) });
  }

  ngOnDestroy(): void {
    this.svgPreviewCache.clear();
    this.svgPreviewLoading.clear();
  }

  get fields(): FormArray<FormGroup> {
    return this.form.get('fields') as FormArray<FormGroup>;
  }
  // #endregion State

  // ----------feature 1 start-------------
  // #region Feature 1: Utils and onClick (Form & Drawer)
  openDrawer(isEdit: boolean = false) {
    // Open fresh for add; keep state for edit
    if (!isEdit) {
      this.editingId = undefined;
      this.clearPreviews();
      (this.fields.controls as FormGroup[]).forEach((fg) => {
        const def = this.initialValueFor({
          key: fg.get('key')!.value,
          fieldType: fg.get('fieldType')!.value as any,
          dataType: fg.get('dataType')!.value as any,
        } as FieldSpec);
        fg.get('value')!.reset(def);
      });
    }
    this.isDrawerVisible = true;
  }
  closeDrawer() {
    // Also clear editing context when closing without save
    this.editingId = undefined;
    this.clearPreviews();
    (this.fields.controls as FormGroup[]).forEach((fg) => {
      const def = this.initialValueFor({
        key: fg.get('key')!.value,
        fieldType: fg.get('fieldType')!.value as any,
        dataType: fg.get('dataType')!.value as any,
      } as FieldSpec);
      fg.get('value')!.reset(def);
    });
    this.isDrawerVisible = false;
  }
  // #endregion Feature 1: Utils and onClick (Form & Drawer)

  async ready() {
    if (this.user) {
      this.route.paramMap.subscribe((p) => {
        const category = p.get('category') as MasterDataCategoryV2;
        this.category = category;
        this.fetchConfig(category);
        this.fetchData(category);
        this.fetchDynamicDropdownOptions();
      });
    }
  }

  fetchDynamicDropdownOptions() {
    forkJoin({
      partners: this.masterDataService.partnerMasterDataList(),
      experts: this.service.listMasterDataByExperts(),
      therapists: this.therapistService.therapistList(),
      listeners: this.listenerService.listenerList({
        export: true,
        page: 1,
        pageSize: 10,
      }),
      masterCategories: this.service.getMasterDataCategories(),
      lyfis: this.masterDataService.lyfisMasterDataList(),
      master: this.masterDataService.masterDataList(),
    }).subscribe({
      next: ({
        partners,
        experts,
        therapists,
        listeners,
        masterCategories,
        lyfis,
        master,
      }) => {
        try {
          // cache raw datasets for category-specific dropdowns
          this.lyfisCache = lyfis;
          this.masterCache = master;
          // 🧩 Partners
          this.partners = partners.map((d) => ({
            data: d.companyName!,
            value: d._id!,
          }));

          // 🧩 Experts
          this.experts = experts.map((d) => ({
            data: d.name,
            value: d.expertId,
          }));

          // 🧩 Therapists
          this.therapists = therapists.map((d) => ({
            data: `${d.firstName} ${d.lastName}`,
            value: d._id!,
          }));

          // 🧩 Listeners
          this.listeners = listeners.data.map((d) => ({
            data: d.name,
            value: d.id,
          }));

          // 🧩 All possible options
          const baseOptions = masterCategories.categories.map((option) => ({
            data: option,
            value: option,
          }));
          this.allPossibleOptions = [
            ...baseOptions,
            { data: 'FromLyfisQuestions', value: 'FromLyfisQuestions' },
            { data: 'Therapist', value: 'Therapist' },
            { data: 'Listener', value: 'Listener' },
            { data: 'Expert', value: 'Expert' },
            { data: 'Partner', value: 'Partner' },
          ];

          // 🧩 ANYO Rules
          this.anyoRules = masterCategories.anyoRules.map((option) => ({
            data: option,
            value: option,
          }));

          // 🧩 Master / Lyfis combined data
          const mergedMasterKeys = Object.keys(
            master,
          ) as (keyof MasterDataResponse)[];
          const masterValues = mergedMasterKeys.flatMap((key) => {
            const m = (master[key] as any[]) || [];
            const l =
              (lyfis[key as keyof LyfisMasterDataResponse] as any[]) || [];
            return m.length > 0 ? m : l;
          });
          this.masterData = masterValues.map((d) => ({
            data: d.data,
            value: d.m_id,
          }));
          // refresh per-field options now that sources are loaded
          this.refreshAllFieldOptions();
        } catch (e) {
          this.toast.showAnyoErrorToast('Error processing dropdown data');
        }
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.toast.showAnyoErrorToast(
          errorBody.description || 'Failed to load dropdown options',
        );
      },
    });
  }

  rawConfig:
    | { category: MasterDataCategoryV2; configFeilds: FieldSpec[] }
    | undefined = undefined;

  // #endregion Feature 1: API calls (Form configuration, dropdown options)
  private fetchConfig(category: MasterDataCategoryV2) {
    this.service.getConfigByCategory(category).subscribe((res) => {
      this.rawConfig = res;
      const arr = new FormArray<FormGroup>([]);
      res.configFeilds.forEach((f, index) => {
        arr.push(
          this.fb.group({
            key: f.key,
            dataType: f.dataType,
            fieldType: f.fieldType,
            dropdownOptions: f.dropdownOptions ?? null,
            customDropdownOptions: this.fb.array(
              Array.isArray(f.customDropdownOptions)
                ? f.customDropdownOptions.map((opt) => this.fb.control(opt))
                : [],
            ),
            value: this.fb.control(this.initialValueFor(f)),
          }),
        );

        this.updateFieldOptionsForIndex(index);
      });
      this.form.setControl('fields', arr);
      this.displayKeys = ['data', ...res.configFeilds.map((c) => c.key)];
      this.filteredKeys = this.displayKeys.filter((k) => k !== 'data');
      if (this.originalList.length > 0) {
        try {
          this.list = JSON.parse(JSON.stringify(this.originalList));
        } catch {
          this.list = this.originalList.map((r) => ({ ...r }));
        }
        this.replaceIdsWithNames(this.list);
      }
    });
  }

  private buildOptionsFromCustom(customOptions: unknown): DropdownOptions[] {
    let arr: string[] = [];
    if (typeof customOptions === 'string') {
      arr = [customOptions];
    } else if (Array.isArray(customOptions)) {
      arr = customOptions as string[];
    }
    return arr.map((opt) => ({ value: opt, data: opt }));
  }

  private updateFieldOptionsForIndex(index: number) {
    const fg = this.fields.at(index) as FormGroup | undefined;
    if (!fg) return;
    const optRaw = fg.get('dropdownOptions')?.value as unknown;
    // If backend provided a category name (not one of enum values), build options from master/lyfis for that category
    if (
      typeof optRaw === 'string' &&
      !Object.values(DropdownOptionsV2).includes(optRaw as DropdownOptionsV2)
    ) {
      this.fieldOptions[index] = this.buildOptionsForCategory(optRaw);
      return;
    }
    const opt = optRaw as DropdownOptionsV2 | null;
    switch (opt) {
      case DropdownOptionsV2.PARTNER:
        this.fieldOptions[index] = this.partners || [];
        break;
      case DropdownOptionsV2.EXPERT:
        this.fieldOptions[index] = this.experts || [];
        break;
      case DropdownOptionsV2.THERAPIST:
        this.fieldOptions[index] = this.therapists || [];
        break;
      case DropdownOptionsV2.LISTENER:
        this.fieldOptions[index] = this.listeners || [];
        break;
      case DropdownOptionsV2.ALL_POSSIBLE_OPTION:
        this.fieldOptions[index] = this.allPossibleOptions || [];
        break;
      case DropdownOptionsV2.ANYO_RULES:
        this.fieldOptions[index] = this.anyoRules || [];
        break;
      case DropdownOptionsV2.OTHERS: {
        const custom = fg.get('customDropdownOptions')?.value;
        this.fieldOptions[index] = this.buildOptionsFromCustom(custom);
        break;
      }
      default:
        this.fieldOptions[index] = this.masterData || [];
    }
  }

  private normalizeCategoryKey(key: string): string {
    return key.charAt(0).toLowerCase() + key.slice(1);
  }

  private getCategoryDataFromCache(
    cache: Record<string, unknown> | undefined,
    categoryKey: string,
  ): Array<{ m_id: string; data: string }> {
    if (!cache) return [];
    const keys = [categoryKey, this.normalizeCategoryKey(categoryKey)];
    for (const key of keys) {
      const arr = cache[key];
      if (Array.isArray(arr) && arr.length > 0) {
        return arr as Array<{ m_id: string; data: string }>;
      }
    }
    return [];
  }

  private getCategoryData(categoryKey: string): Array<{ m_id: string; data: string }> {
    const caches = [
      this.masterCache as Record<string, unknown> | undefined,
      this.lyfisCache as Record<string, unknown> | undefined,
      this.masterDataArray as Record<string, unknown> | undefined,
      this.lyfisDataArray as Record<string, unknown> | undefined,
    ];
    for (const cache of caches) {
      const arr = this.getCategoryDataFromCache(cache, categoryKey);
      if (arr.length > 0) return arr;
    }
    return [];
  }

  private buildOptionsForCategory(categoryKey: string): DropdownOptions[] {
    const arr = this.getCategoryData(categoryKey);
    return arr.map((d) => ({ data: d.data, value: d.m_id }));
  }

  private refreshAllFieldOptions() {
    (this.fields.controls as FormGroup[]).forEach((_, idx) =>
      this.updateFieldOptionsForIndex(idx),
    );
  }

  onFile(event: Event, i: number) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (file) {
      (this.fields.at(i) as FormGroup).get('value')!.setValue(file);
      // update preview
      const prev = this.previewUrls[i];
      if (prev && prev.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev);
        } catch (e) {
          /* ignore revoke errors */
        }
      }
      this.previewUrls[i] = URL.createObjectURL(file);
      if (this.isSvgFile(file)) {
        this.loadSvgTextIntoCache(this.previewUrls[i], file);
      }
    }
  }

  async saveMediaFiles(file: File, pathName: string, toastText: string) {
    const uuid = uuidv4();
    const uuidWithoutHyphens = uuid.replace(/-/g, '');
    const extension = file.name.split('.').pop()?.toLowerCase();
    const filePath = `${pathName}/${uuidWithoutHyphens}.${extension}`;
    const contentType =
      file.type ||
      (extension === 'svg' ? 'image/svg+xml' : 'application/octet-stream');
    const uploadUrl = await lastValueFrom(
      this.fileService.generateUploadUrl(
        AnyoS3Buckets.CDN_BUCKET,
        filePath,
        contentType,
      ),
    );
    this.toast.showSuccess(`Uploading ${toastText}`);
    await lastValueFrom(this.fileService.uploadFile(file, uploadUrl.url));
    this.toast.showSuccess(`Uploading ${toastText} completed`);
    return `https://cdn.anyo.app/${filePath}`;
  }

  async submit() {
    const payload: Record<string, unknown> = {};
    for (const fg of this.fields.controls as FormGroup[]) {
      if (
        fg.value.fieldType === 'file_picker' &&
        fg.value.value instanceof File
      ) {
        const url = await this.saveMediaFiles(
          fg.value.value,
          'master-data/' + this.category.toLowerCase(),
          fg.value.key,
        );
        payload[fg.get('key')!.value] = url;
      } else {
        payload[fg.get('key')!.value] = fg.get('value')!.value;
      }
    }
    this.isSubmitting = true;
    this.service
      .createMasterData({
        category: this.category,
        fields: { ...payload, m_id: this.editingId },
      })
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.closeDrawer();
          this.editingId = undefined;
          this.fetchData(this.category);
          // Reset only values, keep form structure for next add
          (this.fields.controls as FormGroup[]).forEach((fg) => {
            const def = this.initialValueFor({
              key: fg.get('key')!.value,
              fieldType: fg.get('fieldType')!.value as any,
              dataType: fg.get('dataType')!.value as any,
            } as FieldSpec);
            fg.get('value')!.reset(def);
          });
          this.clearPreviews();
        },
        error: (error) => {
          const e = error as IAnyoError;
          this.toast.showAnyoErrorToast(e.description);
          this.isSubmitting = false;
        },
      });
  }

  private initialValueFor(f: FieldSpec) {
    if (f.fieldType === 'switch') return false;
    if (f.dataType === 'number') return null;
    if (f.fieldType === 'color_picker') return '#000000';
    if (f.dataType === 'StringArray') return [];
    return '';
  }

  private clearPreviews() {
    Object.keys(this.previewUrls).forEach((k) => {
      const url = this.previewUrls[+k];
      if (url && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          /* ignore revoke errors */
        }
      }
    });
    this.previewUrls = {};
  }
  // ----------feature 1 end--------------

  // ----------feature 2 start-------------
  // #region Feature 2: API calls (Table data, categories and master data lists)

  masterDataArray: MasterDataResponse | undefined = undefined;
  lyfisDataArray: LyfisMasterDataResponse | undefined = undefined;
  async fetchData(category: string) {
    this.tableLoading = true;

    forkJoin({
      lyfis: this.masterDataService.lyfisMasterDataList(),
      master: this.masterDataService.masterDataList(),
    }).subscribe(
      ({
        lyfis,
        master,
      }: {
        lyfis: LyfisMasterDataResponse;
        master: MasterDataResponse;
      }) => {
        this.masterDataArray = master;
        this.lyfisDataArray = lyfis;
        const masterData = (master?.[
          (category.charAt(0).toLowerCase() +
            category.slice(1)) as keyof MasterDataResponse
        ] || []) as Array<{
          m_id: string;
          data: string;
          [key: string]: unknown;
        }>;
        const lyfisData = (lyfis?.[category as keyof LyfisMasterDataResponse] ||
          []) as unknown as Array<{
          m_id: string;
          data: string;
          [key: string]: unknown;
        }>;
        this.list =
          masterData.length > 0
            ? masterData
            : lyfisData.length > 0
              ? lyfisData
              : [];

        // Keep a pristine copy (IDs intact) for edit prefills
        try {
          this.originalList = JSON.parse(JSON.stringify(this.list));
        } catch {
          // fallback to shallow copy if JSON clone fails
          this.originalList = this.list.map((r) => ({ ...r }));
        }

        this.replaceIdsWithNames(this.list);
        this.prefetchSvgPreviews(this.list);
        this.tableLoading = false;
      },
    );
  }
  // #endregion Feature 2: API calls (Table data, categories and master data lists)

  // ----------feature 2 start-------------
  isUrl(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    try {
      const u = new URL(trimmed);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  }

  mediaUrl(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  isSvgUrl(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    return /\.svg($|[?#])/i.test(value.trim());
  }

  isSvgFile(file: File): boolean {
    return file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
  }

  isSvgPreview(value: unknown): boolean {
    if (value instanceof File) return this.isSvgFile(value);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('blob:')) {
        return (
          this.svgPreviewCache.has(trimmed) ||
          this.svgPreviewLoading.has(trimmed)
        );
      }
      return this.isSvgUrl(trimmed);
    }
    return false;
  }

  svgPreviewSource(index: number, value: unknown): string {
    if (this.previewUrls[index]) return this.previewUrls[index];
    return typeof value === 'string' && this.isUrl(value) ? value : '';
  }

  showSvgPreview(index: number, value: unknown): boolean {
    const source = this.svgPreviewSource(index, value);
    if (source) return this.isSvgPreview(source);
    return value instanceof File && this.isSvgFile(value);
  }

  getSvgPreview(value: unknown): SafeHtml | null {
    if (typeof value !== 'string') return null;
    const url = this.mediaUrl(value);
    if (!url || !this.isSvgPreview(value)) return null;

    if (!this.svgPreviewCache.has(url) && !this.svgPreviewLoading.has(url)) {
      this.loadSvgFromUrl(url);
    }

    return this.svgPreviewCache.get(url) ?? null;
  }

  private prefetchSvgPreviews(
    list: Array<{ m_id: string; data: string; [key: string]: unknown }>,
  ): void {
    list.forEach((row) => {
      this.queueSvgPreview(row.data);
      Object.values(row).forEach((val) => {
        if (typeof val === 'string') {
          this.queueSvgPreview(val);
        } else if (Array.isArray(val)) {
          val.forEach((item) => this.queueSvgPreview(item));
        }
      });
    });
  }

  private queueSvgPreview(value: unknown): void {
    const url = this.mediaUrl(value);
    if (!url || !this.isSvgUrl(url)) return;
    if (this.svgPreviewCache.has(url) || this.svgPreviewLoading.has(url)) {
      return;
    }
    this.loadSvgFromUrl(url);
  }

  private loadSvgFromUrl(url: string): void {
    this.svgPreviewLoading.add(url);
    fetch(url, { mode: 'cors', credentials: 'omit' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load SVG (${response.status})`);
        }
        return response.text();
      })
      .then((svg) => {
        this.svgPreviewCache.set(
          url,
          this.sanitizer.bypassSecurityTrustHtml(
            sanitizeSvg(this.normalizeSvgForPreview(svg)),
          ),
        );
      })
      .catch(() => {
        this.svgPreviewCache.set(
          url,
          this.sanitizer.bypassSecurityTrustHtml(''),
        );
      })
      .finally(() => {
        this.svgPreviewLoading.delete(url);
        this.cdr.markForCheck();
      });
  }

  private loadSvgTextIntoCache(cacheKey: string, file: File): void {
    this.svgPreviewLoading.add(cacheKey);
    const reader = new FileReader();
    reader.onload = () => {
      this.svgPreviewCache.set(
        cacheKey,
        this.sanitizer.bypassSecurityTrustHtml(
          sanitizeSvg(this.normalizeSvgForPreview(String(reader.result ?? ''))),
        ),
      );
      this.svgPreviewLoading.delete(cacheKey);
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.svgPreviewLoading.delete(cacheKey);
      this.cdr.markForCheck();
    };
    reader.readAsText(file);
  }

  private normalizeSvgForPreview(svg: string): string {
    return svg.replace(/<svg\b/i, '<svg width="100%" height="100%" ');
  }

  valueType(value: unknown): 'svg' | 'image' | 'images' | 'array' | 'text' {
    if (typeof value === 'string') {
      if (!this.isUrl(value)) return 'text';
      return this.isSvgUrl(value) ? 'svg' : 'image';
    }
    if (Array.isArray(value)) {
      const allStrings = value.every((v) => typeof v === 'string');
      const allUrls = allStrings && value.every((v) => this.isUrl(v as string));
      return allUrls ? 'images' : 'array';
    }
    return 'text';
  }

  cellType(
    key: string,
    value: unknown,
  ): 'color' | 'svg' | 'image' | 'images' | 'array' | 'text' {
    if (this.isColorColumn(key)) return 'color';
    return this.valueType(value);
  }

  colorValue(value: unknown): string {
    const formatted = this.formatValue(value).trim();
    return formatted || 'transparent';
  }

  formatValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value === 'undefined' ? '' : value;
    if (Array.isArray(value)) return value.join(', ');
    try {
      return String(value);
    } catch {
      return '';
    }
  }

  toArray(value: unknown): any[] {
    return Array.isArray(value) ? (value as any[]) : [];
  }

  isArrayColumn(key: string): boolean {
    const field = this.rawConfig?.configFeilds.find((f) => f.key === key);
    return field?.dataType === 'StringArray';
  }

  isColorColumn(key: string): boolean {
    const field = this.rawConfig?.configFeilds.find((f) => f.key === key);
    if (field?.fieldType === 'color_picker') return true;
    return /color$/i.test(key);
  }

  // #region Feature 2: Utils and onClick (Table rendering and mapping)
  editItem(row: { m_id: string; [key: string]: unknown }) {
    this.editingId = row.m_id;
    this.openDrawer(true);
    const original = this.originalList.find((r) => r.m_id === row.m_id);
    (this.fields.controls as FormGroup[]).forEach((fg, index) => {
      const key = fg.get('key')!.value as string;
      // Prefer original (IDs intact) for setting control values; fallback to displayed row
      const v =
        original && original[key] !== undefined ? original[key] : row[key];
      const def = this.initialValueFor({
        key,
        fieldType: fg.get('fieldType')!.value as any,
        dataType: fg.get('dataType')!.value as any,
      } as FieldSpec);
      if (v !== undefined && v !== null) {
        fg.get('value')!.setValue(v);
        if (fg.get('fieldType')!.value === 'file_picker') {
          if (typeof v === 'string' && this.isUrl(v)) {
            this.previewUrls[index] = v;
            this.queueSvgPreview(v);
          } else {
            delete this.previewUrls[index];
          }
        }
      } else {
        fg.get('value')!.setValue(def);
        if (fg.get('fieldType')!.value === 'file_picker') {
          delete this.previewUrls[index];
        }
      }
    });
  }
  // #endregion Feature 2: Utils and onClick (Table rendering and mapping)

  // #endregion Feature 1: Utils and onClick (Form & Drawer - continued)

  // #region Feature 2: Utils and onClick (Table rendering and mapping - continued)

  // 1. Function to extract unresolved string IDs for generic API lookup
  extractIdsFromList(
    list: Array<{ m_id: string; data: string; [key: string]: unknown }>,
  ): string[] {
    const ids: string[] = [];
    list.forEach((row) => {
      Object.entries(row).forEach(([key, val]) => {
        if (key === 'm_id' || key === 'data') return;
        const configField = this.rawConfig?.configFeilds.find(
          (f) => f.key === key,
        );
        if (configField?.dropdownOptions) return;
        if (typeof val === 'string' && this.isObjectIdString(val)) {
          ids.push(val);
        } else if (Array.isArray(val)) {
          val.forEach((v) => {
            if (typeof v === 'string' && this.isObjectIdString(v)) {
              ids.push(v);
            }
          });
        }
      });
    });
    return [...new Set(ids)];
  }

  private applyConfigBasedNameResolution(
    list: Array<{ m_id: string; data: string; [key: string]: unknown }>,
  ): void {
    list.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const val = row[key];
        if (
          typeof val === 'string' &&
          (this.isUrl(val) || this.isSvgUrl(val))
        ) {
          return;
        }
        if (
          Array.isArray(val) &&
          val.every((v) => typeof v === 'string' && this.isUrl(v))
        ) {
          return;
        }
        const configField = this.rawConfig?.configFeilds.find(
          (f) => f.key === key,
        );
        const config = configField?.dropdownOptions;
        if (!config) return;
        const lookupKey = typeof config === 'string' ? config : key;
        const factsArr = this.getCategoryData(lookupKey);
        const factsMap = new Map(factsArr.map((f) => [f.m_id, f.data]));
        if (factsMap.size === 0) return;
        if (typeof val === 'string') {
          row[key] = factsMap.get(val) ?? val;
        } else if (Array.isArray(val)) {
          row[key] = val.map((v) =>
            typeof v === 'string' && factsMap.has(v) ? factsMap.get(v) : v,
          );
        }
      });
    });
  }

  // 2. Resolve config references locally, then call API only for remaining IDs
  replaceIdsWithNames(
    list: Array<{ m_id: string; data: string; [key: string]: unknown }>,
  ) {
    this.applyConfigBasedNameResolution(list);
    const ids = this.extractIdsFromList(list);

    if (!ids.length) {
      this.cdr.markForCheck();
      return;
    }

    forkJoin({
      names: this.service.getNameByAnyId(ids),
    }).subscribe({
      next: ({ names }) => {
        const idMap = new Map(
          (names as Array<{ id: string; name: string }>).map((item) => [
            item.id,
            item.name,
          ]),
        );
        list.forEach((row) => {
          Object.entries(row).forEach(([key, val]) => {
            if (key === 'm_id' || key === 'data') return;
            const configField = this.rawConfig?.configFeilds.find(
              (f) => f.key === key,
            );
            if (configField?.dropdownOptions) return;
            if (typeof val === 'string' && idMap.has(val)) {
              row[key] = idMap.get(val);
            } else if (Array.isArray(val)) {
              row[key] = val.map((v) =>
                typeof v === 'string' && idMap.has(v) ? idMap.get(v) : v,
              );
            }
          });
        });
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error(err);
        this.toast.showAnyoErrorToast('Failed to fetch names');
      },
    });
  }

  // Utility function
  isObjectIdString(value: string): boolean {
    return /^[0-9a-fA-F]{24}$/.test(value);
  }
  // #endregion Feature 2: Utils and onClick (Table rendering and mapping)

  // ----------feature 2 end--------------
}
